import React, {useEffect, useState} from 'react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {VJFPUserService} from '../../../services/VJFPUserService'
import {useAuth} from 'app/context/auth-context'
import {Card} from 'react-bootstrap-v5'
import {CardBody, CardHeader} from 'react-bootstrap'
import {AppTopNav} from '../AppTopNav'
import {BasePageProps, Roles} from 'app/types'
import useIsMounted from 'app/hooks/useIsMounted'
import {AlertModal} from '../modals/AlertModal'
import {UrlParamReplace} from 'app/helpers/MiscUtils'
import {JSRoutes} from 'app/modules/job-seeker/JSPage'
import {useHistory} from 'react-router-dom'
import {EmployerAdminRoutes} from 'app/modules/employer-admin/EmployerAdminPage'
import {RecruiterRoutes} from 'app/modules/recruiter/RecruiterPage'
import {AdminRoutes} from 'app/modules/admin/AdminPage'
import {Storage, Auth} from 'aws-amplify'
import {ResumeService} from 'app/services/resumeService'
import {COLLEGE_MAJORS} from '../college-majors/majors-list'
import Cropper from 'react-easy-crop'
import 'react-easy-crop/react-easy-crop.css'
import {Modal} from 'react-bootstrap'
import {Worker, Viewer} from '@react-pdf-viewer/core'
import '@react-pdf-viewer/core/lib/styles/index.css'
import {useS3Object} from 'app/context/S3ObjectContext'

const baseSchema = {
  firstName: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('First name is required'),
  lastName: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('Last name is required'),
  phone_number: Yup.string()
    .required('Phone number is required')
    .matches(/^[+][1][0-9]{10}$/, 'Phone number must be in format +19999999999'),
  avatar: Yup.mixed(),
  email: Yup.string().email('Invalid email format'),
  smsConsent: Yup.boolean(),
}

const applicantSchema = {
  ...baseSchema,
  linkedIn_url: Yup.string().url('Must be a valid URL'),
  years_of_experience: Yup.number()
    .required('Years of experience is required')
    .min(0, 'Must be at least 0')
    .max(50, 'Must be less than 50'),
  salary_requirements: Yup.string().required('Salary requirements is required'),
  geographic_preference: Yup.string().required('Geographic preference is required'),

  security_clearance: Yup.string().required('Security clearance is required'),
  willing_to_relocate: Yup.boolean().required('Relocation preference is required'),
  degree_type: Yup.string().required('Degree type is required'),
  academic_major: Yup.string().required('Academic major is required'),
  academic_major_other: Yup.string().when('academic_major', {
    is: 'Other',
    then: Yup.string().required('Please specify your academic major'),
    otherwise: Yup.string(),
  }),
  field_of_work: Yup.string().required('Field of work is required'),
  field_of_work_other: Yup.string().when('field_of_work', {
    is: 'Other',
    then: Yup.string().required('Please specify your field of work'),
    otherwise: Yup.string(),
  }),
  availability_date: Yup.string().required('Availability date is required'),
  resume_url: Yup.string().required('Resume is required'),
}

const profileSchema = (roles: string[] = []) => {
  return Yup.object().shape(roles.includes('Applicant') ? applicantSchema : baseSchema)
}

const degreeTypes = ['High School', 'Associates', 'Bachelors', 'Masters', 'Doctorate']
const salaryRanges = ['$50,000-$75,000', '$75,000-$100,000', '$100,000-$150,000', '$150,000+']
const securityClearanceOptions = ['None', 'Secret', 'Top Secret', 'TS/SCI', 'DoE L', 'DoE Q']

const geographicRegions = [
  'Northeast',
  'Northwest',
  'Southeast',
  'Southwest',
  'Central',
  'MidAtlantic', // Added new region
  'Mountain',
  'Pacific',
  'All',
]

const formatPhoneNumber = (value: string) => {
  if (!value) return value
  const phoneNumber = value.replace(/[^\d]/g, '')
  const phoneNumberLength = phoneNumber.length
  if (phoneNumberLength < 4) return phoneNumber
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`
}

const getCroppedImg = (imageSrc: string, crop: any, outputType: string): Promise<File> => {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.src = imageSrc
    image.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = 128
      canvas.height = 128
      const ctx = canvas.getContext('2d')
      if (!ctx) return reject(new Error('no ctx'))

      // If it's JPEG, we might want to fill with white to avoid black background on transparent areas
      // but since we want to support transparency for PNG, we only do this if output is NOT PNG
      if (outputType !== 'image/png') {
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, 128, 128)
      }

      ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, 128, 128)

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const ext = outputType === 'image/png' ? 'png' : 'jpg'
            const file = new File([blob], `avatar.${ext}`, {type: outputType})
            resolve(file)
          }
        },
        outputType,
        0.8
      )
    }
    image.onerror = reject
  })
}

export const ManageProfile = (props: BasePageProps) => {
  const {user} = useAuth()
  const [loading, setLoading] = useState(false)
  const vjfpUserService = new VJFPUserService()
  const resumeService = new ResumeService()
  const history = useHistory()
  const [showSavedSuccessfullyModal, setShowSavedSuccessfullyModal] = useState<boolean>(false)
  const isMounted = useIsMounted('ManageProfile')
  const [isResumeUploading, setIsResumeUploading] = useState(false)
  const [isResumeConverting, setIsResumeConverting] = useState(false)
  const [resumeFileName, setResumeFileName] = useState<string | null>(null)
  const [resumePDFUrl, setResumePDFUrl] = useState<string>('')
  const [showResumePreview, setShowResumePreview] = useState(false)
  const [existingAvatarKey, setExistingAvatarKey] = useState<string>('')
  const {getS3ObjectUrl, s3ObjectCache, updateS3ObjectCache, invalidateS3ObjectCache} =
    useS3Object()
  const currentAvatarUrl = existingAvatarKey ? s3ObjectCache[existingAvatarKey] : ''
  const [imageToCrop, setImageToCrop] = useState<string | null>(null)
  const [crop, setCrop] = useState({x: 0, y: 0})
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null)
  const [originalFileType, setOriginalFileType] = useState<string>('image/jpeg')

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const rawValue = value.replace(/[^\d]/g, '')
    // If the user pasted a number starting with 1, we strip it because we prepend +1
    const cleanValue =
      rawValue.startsWith('1') && rawValue.length > 10 ? rawValue.slice(1) : rawValue
    const limitedValue = cleanValue.slice(0, 10)
    formik.setFieldValue('phone_number', limitedValue ? `+1${limitedValue}` : '')
  }

  const getSubmitButtonText = () => {
    if (user?.roles?.includes(Roles.Applicant)) return 'Save Changes and enter Job Fair Lobby'
    if (user?.roles?.includes(Roles.Recruiter)) return 'Save Changes and enter Booth'
    if (user?.roles?.includes(Roles.EmployerAdmin)) return 'Save Changes and go to Dashboard'
    if (user?.roles?.includes(Roles.Admin)) return 'Save Changes and go to Companies list'
    return 'Save Changes'
  }

  const initialValues = {
    firstName: '',
    username: '',
    lastName: '',
    email: '',
    phone_number: '',
    avatar: null as File | null,
    linkedIn_url: 'https://linkedin.com/',
    degree_type: '',
    academic_major: '',
    academic_major_other: '',
    field_of_work: '',
    field_of_work_other: '',
    years_of_experience: 0,
    salary_requirements: '',
    resume_url: '',
    availability_date: '',
    geographic_preference: '',
    security_clearance: '',
    willing_to_relocate: false,
    smsConsent: false,
  }

  const handleResumeChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.currentTarget.files
    if (files && files.length > 0) {
      const file = files[0]
      setIsResumeUploading(true)
      setIsResumeConverting(false)
      try {
        const fileExtension = file.name.split('.').pop()
        const resumePath = await Storage.put(
          `attached_resumes/${user?.id}_resume.${fileExtension}`,
          file,
          {
            contentType: file.type,
          }
        )
        invalidateS3ObjectCache(resumePath.key)
        setResumeFileName(file.name)
        setIsResumeUploading(false)

        // Convert to PDF if uploaded format is not already PDF
        // This must happen BEFORE setting formik value so the PDF effect finds the converted file
        if (fileExtension?.toLowerCase() !== 'pdf') {
          setIsResumeConverting(true)
          try {
            await Storage.remove(`attached_resumes/${user?.id}_resume.pdf`)
          } catch (_) {
            // No existing PDF to remove
          }
          try {
            await resumeService.convertDocument(`public/${resumePath.key}`)
          } catch (err) {
            console.error('Error converting resume to PDF:', err)
          }
          setIsResumeConverting(false)
        }

        formik.setFieldValue('resume_url', resumePath.key)
      } catch (err) {
        console.error('Error uploading resume:', err)
      } finally {
        setIsResumeUploading(false)
        setIsResumeConverting(false)
      }
    }
  }

  const formik = useFormik({
    initialValues,
    validationSchema: profileSchema(user?.roles),
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      if (!user?.organizationId) return
      setLoading(true)
      try {
        let avatarKey = existingAvatarKey

        if (values.avatar) {
          const ext = values.avatar.type === 'image/png' ? 'png' : 'jpg'
          const key = `${user.organizationId}/users/${user.id}/avatar.${ext}`
          await Storage.put(key, values.avatar, {
            contentType: values.avatar.type,
          })

          invalidateS3ObjectCache(key)

          // Update the global avatar cache with the local blob URL for immediate feedback
          const localUrl = URL.createObjectURL(values.avatar)
          updateS3ObjectCache(key, localUrl)

          avatarKey = key
        }

        await vjfpUserService.updateProfile(user.organizationId, user.id, {
          username: values?.username || '',
          firstName: values.firstName || '',
          lastName: values.lastName || '',
          emailId: values.email || '',
          phone_number: values.phone_number || '',
          linkedIn_url: values.linkedIn_url || 'https://linkedin.com/',
          degree_type: values.degree_type || '',
          academic_major:
            values.academic_major === 'Other'
              ? values.academic_major_other
              : values.academic_major || '',
          field_of_work:
            values.field_of_work === 'Other'
              ? values.field_of_work_other
              : values.field_of_work || '',
          years_of_experience: values.years_of_experience || 0,
          salary_requirements: values.salary_requirements || '',
          resume_url: values.resume_url || '',
          availability_date: values.availability_date || '',
          geographic_preference: values.geographic_preference || '',
          security_clearance: values.security_clearance || '',
          willing_to_relocate: values.willing_to_relocate || false,
          smsConsent: values.smsConsent || false,
          isProfileComplete: true,
          avatar: avatarKey,
        })

        setShowSavedSuccessfullyModal(true)
      } catch (error) {
        console.error('Error updating profile:', error)
      } finally {
        setLoading(false)
      }
    },
  })

  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user?.id || !user?.organizationId) return
      const userProfile = await vjfpUserService.getById(user.organizationId, user.id)
      if (userProfile && isMounted()) {
        formik.setValues({
          firstName: userProfile.firstName || '',
          username: userProfile.username || '',
          lastName: userProfile.lastName || '',
          email: userProfile.emailId || '',
          phone_number: userProfile.phone_number || '',
          avatar: null,
          linkedIn_url: userProfile.linkedIn_url || 'https://linkedin.com/',
          degree_type: userProfile.degree_type || '',
          academic_major:
            (userProfile.academic_major && COLLEGE_MAJORS.includes(userProfile.academic_major)) ||
            !userProfile.academic_major
              ? userProfile.academic_major || ''
              : 'Other',
          academic_major_other:
            (userProfile.academic_major && COLLEGE_MAJORS.includes(userProfile.academic_major)) ||
            !userProfile.academic_major
              ? ''
              : userProfile.academic_major || '',
          field_of_work:
            (userProfile.field_of_work && COLLEGE_MAJORS.includes(userProfile.field_of_work)) ||
            !userProfile.field_of_work
              ? userProfile.field_of_work || ''
              : 'Other',
          field_of_work_other:
            (userProfile.field_of_work && COLLEGE_MAJORS.includes(userProfile.field_of_work)) ||
            !userProfile.field_of_work
              ? ''
              : userProfile.field_of_work || '',
          years_of_experience: userProfile.years_of_experience || 0,
          salary_requirements: userProfile.salary_requirements || '',
          resume_url: userProfile.resume_url || '',
          availability_date: userProfile.availability_date || '',
          geographic_preference: userProfile.geographic_preference || '',
          security_clearance: userProfile.security_clearance || '',
          willing_to_relocate: userProfile.willing_to_relocate || false,
          smsConsent: userProfile.smsConsent || false,
        })
        if (userProfile.avatar) {
          setExistingAvatarKey(userProfile.avatar)
          getS3ObjectUrl(userProfile.avatar)
        }
      }
    }
    loadUserProfile()
  }, [user])

  useEffect(() => {
    let cancelled = false
    const loadResumePdfUrl = async () => {
      if (formik.values.resume_url) {
        const fileExtension = formik.values.resume_url.split('.').pop()
        try {
          if (fileExtension?.toLowerCase() === 'pdf') {
            const url = await Storage.get(formik.values.resume_url)
            if (!cancelled) setResumePDFUrl(url)
          } else {
            const pdfKey = `attached_resumes/${user?.id}_resume.pdf`
            const listResult = await Storage.list(pdfKey)
            if (listResult && Array.isArray(listResult) && listResult.length > 0 && listResult[0]?.key) {
              const pdfUrl = await Storage.get(listResult[0].key)
              if (!cancelled) setResumePDFUrl(pdfUrl)
            } else {
              if (!cancelled) setResumePDFUrl('')
            }
          }
        } catch (_) {
          if (!cancelled) setResumePDFUrl('')
        }
      }
    }
    loadResumePdfUrl()
    return () => { cancelled = true }
  }, [formik.values.resume_url, user?.id])

  useEffect(() => {
    if (formik.submitCount > 0 && !formik.isValid) {
      const errorKeys = Object.keys(formik.errors)
      const firstErrorKey = errorKeys[0]
      if (firstErrorKey) {
        const element =
          document.getElementsByName(firstErrorKey)[0] || document.getElementById(firstErrorKey)
        if (element) {
          element.scrollIntoView({behavior: 'smooth', block: 'center'})
        } else if (firstErrorKey === 'resume_url') {
          const resumeSection = document.getElementById('resume-section')
          if (resumeSection) {
            resumeSection.scrollIntoView({behavior: 'smooth', block: 'center'})
          }
        }
      }

      // Force touch fields to show errors
      errorKeys.forEach((key) => {
        formik.setFieldTouched(key, true, true)
      })
    }
  }, [formik.submitCount, formik.errors, formik.isValid])

  return (
    <div className='chatPage'>
      <AppTopNav title={props.title} breadcrumbs={props.breadcrumbs}></AppTopNav>
      <Card className='mb-5 mb-xl-10'>
        <CardHeader className='border-0 cursor-pointer'>
          <div className='card-title m-0'>
            <h3 className='fw-bolder m-0'>Profile Details</h3>
          </div>
        </CardHeader>
        <CardBody className='py-4'>
          <div className='container-fluid'>
            <form className='form mw-1000px' onSubmit={formik.handleSubmit} noValidate>
              <div className='card-body p-9'>
                {/* Basic Info Section */}
                <div className='row align-items-center mb-6'>
                  <label className='col-lg-3 col-form-label required fw-bold fs-6'>Full Name</label>
                  <div className='col-lg-6'>
                    <div className='row'>
                      {/* First Name Input */}
                      <div className='col-lg-6 fv-row'>
                        <input
                          type='text'
                          className={`form-control form-control-lg form-control-solid mb-3 mb-lg-0 ${
                            formik.errors.firstName && formik.touched.firstName ? 'is-invalid' : ''
                          }`}
                          placeholder='First name'
                          {...formik.getFieldProps('firstName')}
                        />
                        {formik.errors.firstName && formik.touched.firstName && (
                          <div className='invalid-feedback d-block'>{formik.errors.firstName}</div>
                        )}
                      </div>
                      {/* Last Name Input */}
                      <div className='col-lg-6 fv-row'>
                        <input
                          type='text'
                          className={`form-control form-control-lg form-control-solid ${
                            formik.errors.lastName && formik.touched.lastName ? 'is-invalid' : ''
                          }`}
                          placeholder='Last name'
                          {...formik.getFieldProps('lastName')}
                        />
                        {formik.errors.lastName && formik.touched.lastName && (
                          <div className='invalid-feedback d-block'>{formik.errors.lastName}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className='row align-items-center mb-6'>
                  <label className='col-lg-3 col-form-label required fw-bold fs-6'>Email</label>
                  {/* Email Input */}
                  <div className='col-lg-6'>
                    <input
                      type='email'
                      className={`form-control form-control-lg form-control-solid ${
                        formik.errors.email && formik.touched.email ? 'is-invalid' : ''
                      }`}
                      placeholder='Email'
                      {...formik.getFieldProps('email')}
                      readOnly
                    />
                    {formik.errors.email && formik.touched.email && (
                      <div className='invalid-feedback d-block'>{formik.errors.email}</div>
                    )}
                  </div>
                </div>

                <div className='row align-items-center mb-6'>
                  <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                    Phone Number
                  </label>
                  <div className='col-lg-6'>
                    <div className='input-group input-group-solid'>
                      <span
                        className='input-group-text border-0 text-muted'
                        style={{backgroundColor: '#f3f6f9'}}
                      >
                        +1
                      </span>
                      <input
                        type='text'
                        className={`form-control form-control-lg form-control-solid border-start-0 ${
                          formik.errors.phone_number && formik.touched.phone_number
                            ? 'is-invalid'
                            : ''
                        }`}
                        placeholder='(xxx) xxx-xxxx'
                        value={formatPhoneNumber(
                          formik.values.phone_number?.replace('+1', '') || ''
                        )}
                        onChange={handlePhoneChange}
                        onBlur={formik.handleBlur}
                        name='phone_number'
                      />
                    </div>
                    {formik.errors.phone_number && formik.touched.phone_number && (
                      <div className='invalid-feedback d-block'>{formik.errors.phone_number}</div>
                    )}
                  </div>
                </div>

                <div className='row align-items-center mb-6'>
                  <label className='col-lg-3 col-form-label fw-bold fs-6'>
                    Opt in for SMS communication
                  </label>
                  <div className='col-lg-6 fv-row'>
                    <div className='form-check form-switch form-check-custom form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id='smsConsent'
                        checked={formik.values.smsConsent}
                        onChange={(e) => {
                          formik.setFieldValue('smsConsent', e.target.checked)
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className='row align-items-center mb-6'>
                  <label className='col-lg-3 col-form-label fw-bold fs-6'>Photo</label>
                  <div className='col-lg-6'>
                    <div className='d-flex align-items-center'>
                      <div className='symbol symbol-60px me-5'>
                        {formik.values.avatar ? (
                          <img
                            src={URL.createObjectURL(formik.values.avatar)}
                            alt='Avatar Preview'
                          />
                        ) : currentAvatarUrl ? (
                          <img
                            src={currentAvatarUrl}
                            crossOrigin='anonymous'
                            alt='Current Avatar'
                          />
                        ) : (
                          <span className='symbol-label bg-light-primary text-primary fw-bolder fs-2'>
                            {`${formik.values.firstName?.charAt(0) || ''}${
                              formik.values.lastName?.charAt(0) || ''
                            }`.toUpperCase() || 'U'}
                          </span>
                        )}
                      </div>
                      <label htmlFor='avatar' className='btn btn-sm btn-light-primary me-3'>
                        Change Photo
                        <input
                          id='avatar'
                          name='avatar'
                          type='file'
                          accept='image/*'
                          className='d-none'
                          onChange={(event) => {
                            const file = event.currentTarget.files?.[0]
                            if (file) {
                              setOriginalFileType(file.type)
                              setImageToCrop(URL.createObjectURL(file))
                            }
                          }}
                        />
                      </label>
                      <span className='form-text text-muted'>
                        Allowed file types: png, jpg, jpeg.
                      </span>
                    </div>
                  </div>
                </div>

                {/* Applicant-specific fields */}
                {user?.roles?.includes('Applicant') && (
                  <>
                    {/* Resume */}
                    <div className='separator separator-dashed my-6'></div>
                    <h3 className='text-dark fw-bolder mb-6'>Resume</h3>
                    <div className='row align-items-center mb-6' id='resume-section'>
                      <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                        Upload Resume
                      </label>
                      <div className='col-lg-6'>
                        <div className='d-flex align-items-center'>
                          <label htmlFor='resume' className='btn btn-primary me-3'>
                            <i className='fas fa-upload me-2'></i>
                            {isResumeUploading
                              ? 'Uploading...'
                              : isResumeConverting
                              ? 'Uploaded'
                              : 'Select Resume'}
                            <input
                              id='resume'
                              name='resume'
                              type='file'
                              accept='.pdf,.doc,.docx,.txt'
                              className='d-none'
                              onChange={handleResumeChange}
                              disabled={isResumeUploading || isResumeConverting}
                            />
                          </label>
                          {resumeFileName && <span className='text-muted'>{resumeFileName}</span>}
                          {isResumeUploading && (
                            <div className='text-info fs-7 mt-2'>
                              <span className='spinner-border spinner-border-sm me-1'></span>
                              Uploading resume...
                            </div>
                          )}
                          {isResumeConverting && (
                            <div className='text-info fs-7 mt-2'>
                              <span className='spinner-border spinner-border-sm me-1'></span>
                              Converting to PDF...
                            </div>
                          )}
                        </div>
                        {!formik.values.resume_url && !isResumeUploading && (
                          <div className='text-danger fs-7 mt-2'>
                            * Resume upload is mandatory to participate in the job fair
                          </div>
                        )}
                        {formik.touched.resume_url && formik.errors.resume_url && (
                          <div className='invalid-feedback d-block'>{formik.errors.resume_url}</div>
                        )}
                        {resumePDFUrl && (
                          <div className='mt-2'>
                            <button
                              type='button'
                              className='btn btn-sm btn-light-primary'
                              onClick={() => setShowResumePreview(true)}
                            >
                              <i className='fas fa-eye me-1'></i> Preview
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className='row align-items-center mb-6'>
                      <label className='col-lg-3 col-form-label fw-bold fs-6'>LinkedIn URL</label>
                      {/* LinkedIn URL Input */}
                      <div className='col-lg-6 fv-row'>
                        <input
                          type='url'
                          className={`form-control form-control-lg form-control-solid ${
                            formik.errors.linkedIn_url && formik.touched.linkedIn_url
                              ? 'is-invalid'
                              : ''
                          }`}
                          placeholder='LinkedIn URL'
                          {...formik.getFieldProps('linkedIn_url')}
                        />
                        {formik.errors.linkedIn_url && formik.touched.linkedIn_url && (
                          <div className='invalid-feedback d-block'>
                            {formik.errors.linkedIn_url}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Education and Experience */}
                    <div className='separator separator-dashed my-6'></div>
                    <h3 className='text-dark fw-bolder mb-6'>Education & Experience</h3>

                    <div className='row align-items-center mb-6'>
                      <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                        Degree Type
                      </label>
                      {/* Degree Type Select */}
                      <div className='col-lg-6 fv-row'>
                        <select
                          className={`form-select form-select-solid form-select-lg ${
                            formik.errors.degree_type && formik.touched.degree_type
                              ? 'is-invalid'
                              : ''
                          }`}
                          {...formik.getFieldProps('degree_type')}
                        >
                          <option value=''>Select Degree</option>
                          {degreeTypes.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                        {formik.errors.degree_type && formik.touched.degree_type && (
                          <div className='invalid-feedback d-block'>
                            {formik.errors.degree_type}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className='row align-items-center mb-6'>
                      <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                        Academic Major
                      </label>
                      {/* Academic Major Select */}
                      <div className='col-lg-6 fv-row'>
                        <select
                          className={`form-select form-select-solid form-select-lg ${
                            formik.errors.academic_major && formik.touched.academic_major
                              ? 'is-invalid'
                              : ''
                          }`}
                          {...formik.getFieldProps('academic_major')}
                        >
                          <option value=''>Select Major</option>
                          <option value='Other'>Other</option>
                          {COLLEGE_MAJORS.map((major) => (
                            <option key={major} value={major}>
                              {major}
                            </option>
                          ))}
                        </select>
                        <div className='text-muted fs-7 mt-1'>
                          Not in the list? Select "Other" to enter manually.
                        </div>
                        {formik.values.academic_major === 'Other' && (
                          <input
                            type='text'
                            className={`form-control form-control-lg form-control-solid mt-3 ${
                              formik.errors.academic_major_other &&
                              formik.touched.academic_major_other
                                ? 'is-invalid'
                                : ''
                            }`}
                            placeholder='Please specify your academic major'
                            {...formik.getFieldProps('academic_major_other')}
                          />
                        )}
                        {formik.errors.academic_major && formik.touched.academic_major && (
                          <div className='invalid-feedback d-block'>
                            {formik.errors.academic_major}
                          </div>
                        )}
                        {formik.errors.academic_major_other &&
                          formik.touched.academic_major_other && (
                            <div className='invalid-feedback d-block'>
                              {formik.errors.academic_major_other}
                            </div>
                          )}
                      </div>
                    </div>

                    {/* Work Preferences */}
                    <div className='separator separator-dashed my-6'></div>
                    <h3 className='text-dark fw-bolder mb-6'>Work Preference</h3>
                    <div className='row align-items-center mb-6'>
                      <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                        Field of Work
                      </label>
                      {/* Field of Work Select */}
                      <div className='col-lg-6 fv-row'>
                        <select
                          className={`form-select form-select-solid form-select-lg ${
                            formik.errors.field_of_work && formik.touched.field_of_work
                              ? 'is-invalid'
                              : ''
                          }`}
                          {...formik.getFieldProps('field_of_work')}
                        >
                          <option value=''>Select Field</option>
                          <option value='Other'>Other</option>
                          {COLLEGE_MAJORS.map((major) => (
                            <option key={major} value={major}>
                              {major}
                            </option>
                          ))}
                        </select>
                        <div className='text-muted fs-7 mt-1'>
                          Not in the list? Select "Other" to enter manually.
                        </div>
                        {formik.values.field_of_work === 'Other' && (
                          <input
                            type='text'
                            className={`form-control form-control-lg form-control-solid mt-3 ${
                              formik.errors.field_of_work_other &&
                              formik.touched.field_of_work_other
                                ? 'is-invalid'
                                : ''
                            }`}
                            placeholder='Please specify your field of work'
                            {...formik.getFieldProps('field_of_work_other')}
                          />
                        )}
                        {formik.errors.field_of_work && formik.touched.field_of_work && (
                          <div className='invalid-feedback d-block'>
                            {formik.errors.field_of_work}
                          </div>
                        )}
                        {formik.errors.field_of_work_other &&
                          formik.touched.field_of_work_other && (
                            <div className='invalid-feedback d-block'>
                              {formik.errors.field_of_work_other}
                            </div>
                          )}
                      </div>
                    </div>

                    <div className='row align-items-center mb-6'>
                      <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                        Years of Experience
                      </label>
                      {/* Years of Experience Input */}
                      <div className='col-lg-6 fv-row'>
                        <input
                          type='number'
                          className={`form-control form-control-lg form-control-solid ${
                            formik.errors.years_of_experience && formik.touched.years_of_experience
                              ? 'is-invalid'
                              : ''
                          }`}
                          {...formik.getFieldProps('years_of_experience')}
                        />
                        {formik.errors.years_of_experience &&
                          formik.touched.years_of_experience && (
                            <div className='invalid-feedback d-block'>
                              {formik.errors.years_of_experience}
                            </div>
                          )}
                      </div>
                    </div>

                    <div className='row align-items-center mb-6'>
                      <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                        Salary Requirements
                      </label>
                      {/* Salary Requirements Select */}
                      <div className='col-lg-6 fv-row'>
                        <select
                          className={`form-select form-select-solid form-select-lg ${
                            formik.errors.salary_requirements && formik.touched.salary_requirements
                              ? 'is-invalid'
                              : ''
                          }`}
                          {...formik.getFieldProps('salary_requirements')}
                        >
                          <option value=''>Select Range</option>
                          {salaryRanges.map((range) => (
                            <option key={range} value={range}>
                              {range}
                            </option>
                          ))}
                        </select>
                        {formik.errors.salary_requirements &&
                          formik.touched.salary_requirements && (
                            <div className='invalid-feedback d-block'>
                              {formik.errors.salary_requirements}
                            </div>
                          )}
                      </div>
                    </div>

                    <div className='row align-items-center mb-6'>
                      <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                        Security Clearance
                      </label>
                      {/* Security Clearance Select */}
                      <div className='col-lg-6 fv-row'>
                        <select
                          className={`form-select form-select-solid form-select-lg ${
                            formik.errors.security_clearance && formik.touched.security_clearance
                              ? 'is-invalid'
                              : ''
                          }`}
                          {...formik.getFieldProps('security_clearance')}
                        >
                          <option value=''>Select Clearance</option>
                          {securityClearanceOptions.map((clearance) => (
                            <option key={clearance} value={clearance}>
                              {clearance}
                            </option>
                          ))}
                        </select>
                        {formik.errors.security_clearance && formik.touched.security_clearance && (
                          <div className='invalid-feedback d-block'>
                            {formik.errors.security_clearance}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className='row align-items-center mb-6'>
                      <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                        Geographic Preference
                      </label>
                      <div className='col-lg-6 fv-row'>
                        <select
                          className={`form-select form-select-solid form-select-lg ${
                            formik.errors.geographic_preference &&
                            formik.touched.geographic_preference
                              ? 'is-invalid'
                              : ''
                          }`}
                          {...formik.getFieldProps('geographic_preference')}
                        >
                          <option value=''>Select Region</option>
                          {geographicRegions.map((region) => (
                            <option key={region} value={region}>
                              {region}
                            </option>
                          ))}
                        </select>
                        {formik.errors.geographic_preference &&
                          formik.touched.geographic_preference && (
                            <div className='invalid-feedback d-block'>
                              {formik.errors.geographic_preference}
                            </div>
                          )}
                      </div>
                    </div>

                    <div className='row align-items-center mb-6'>
                      <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                        Availability Date
                      </label>
                      {/* Availability Date Input */}
                      <div className='col-lg-6 fv-row'>
                        <input
                          type='date'
                          className={`form-control form-control-lg form-control-solid ${
                            formik.errors.availability_date && formik.touched.availability_date
                              ? 'is-invalid'
                              : ''
                          }`}
                          {...formik.getFieldProps('availability_date')}
                        />
                        {formik.errors.availability_date && formik.touched.availability_date && (
                          <div className='invalid-feedback d-block'>
                            {formik.errors.availability_date}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className='row align-items-center mb-6'>
                      <label className='col-lg-3 col-form-label required fw-bold fs-6 d-flex align-items-center'>
                        <i className='bi bi-geo-alt text-primary me-2 fs-5'></i>
                        Willing to Relocate
                      </label>
                      <div className='col-lg-6 fv-row'>
                        <div className='form-check form-switch form-check-custom form-check-solid'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            id='willing_to_relocate'
                            checked={formik.values.willing_to_relocate}
                            onChange={(e) => {
                              formik.setFieldValue('willing_to_relocate', e.target.checked)
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
                <div className='separator separator-dashed my-6'></div>

                <div className='d-flex justify-content-center'>
                  <button type='submit' className='btn btn-primary' disabled={loading}>
                    {!loading && getSubmitButtonText()}
                    {loading && (
                      <span className='indicator-progress' style={{display: 'block'}}>
                        Please wait...{' '}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </CardBody>
      </Card>
      <Modal show={showResumePreview} onHide={() => setShowResumePreview(false)} size='xl' centered>
        <Modal.Header closeButton>
          <Modal.Title>Resume Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{height: '80vh'}}>
          {resumePDFUrl && (
            <Worker workerUrl='https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.6.347/pdf.worker.min.js'>
              <Viewer fileUrl={resumePDFUrl} />
            </Worker>
          )}
        </Modal.Body>
      </Modal>

      <AlertModal
        show={showSavedSuccessfullyModal}
        onOK={() => {
          setShowSavedSuccessfullyModal(false)
          if (user?.roles?.includes(Roles.Applicant)) history.push(UrlParamReplace(JSRoutes.Lobby))
          else if (user?.roles?.includes(Roles.EmployerAdmin))
            history.push(EmployerAdminRoutes.Dashboard)
          else if (user?.roles?.includes(Roles.Recruiter)) {
            history.push(RecruiterRoutes.Booth)
          } else if (user?.roles?.includes(Roles.Admin)) {
            history.push(AdminRoutes.Organizations)
          }
        }}
      >
        Profile Information saved successfully !
      </AlertModal>

      <Modal show={!!imageToCrop} onHide={() => setImageToCrop(null)} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title>Crop Profile Photo</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{height: '400px', position: 'relative'}}>
          {imageToCrop && (
            <Cropper
              image={imageToCrop}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
              onZoomChange={setZoom}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className='btn btn-secondary' onClick={() => setImageToCrop(null)}>
            Cancel
          </button>
          <button
            className='btn btn-primary'
            onClick={async () => {
              if (imageToCrop && croppedAreaPixels) {
                const croppedFile = await getCroppedImg(
                  imageToCrop,
                  croppedAreaPixels,
                  originalFileType
                )
                formik.setFieldValue('avatar', croppedFile)
                setImageToCrop(null)
              }
            }}
          >
            Apply Crop
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
